import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { activeCurrency } from '../datatypes/currency';
import { WithRealtimeConsumer } from '../mixins/with-realtime-consumer';
import { ApiFactory, IProductAvailability } from '../api/easypos-api';
import {
  IActiveOrderSummary,
  ILowStockReport,
  ISalesReport,
} from '../api/model/requests';
import { shopBrandStyles } from '../styles/shop-brand-styles';

const Base = WithRealtimeConsumer(
  WithActiveShopConsumer(WithUserProfileConsumer(LitElement))
);

interface TickerEntry {
  receivedAt: Date;
  eventName: string;
  payload: unknown;
}

// Read-only operator monitor. Pure listener / poller; never issues
// commands. Polls every 20-60s and pauses when the tab is hidden.
// SignalR realtime events update relevant sections immediately.
@customElement('dashboard-page')
export class DashboardPage extends Base {
  @state() private _activeOrders: IActiveOrderSummary[] = [];
  @state() private _salesReport: ISalesReport | null = null;
  @state() private _lowStock: ILowStockReport | null = null;
  @state() private _unavailableItems: IProductAvailability[] = [];
  @state() private _ticker: TickerEntry[] = [];
  @state() private _lastRefreshUtc: Date | null = null;

  private _activeOrdersTimer?: number;
  private _salesTimer?: number;
  private _lowStockTimer?: number;
  private _availabilityTimer?: number;
  private _unsubRealtime: Array<() => void> = [];
  private _onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void this._refreshAll();
      this._startTimers();
    } else {
      this._stopTimers();
    }
  };

  static styles = [
    shopBrandStyles,
    css`
      :host {
        display: block;
      }

      /* ===== Page header ===== */
      .page-head {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 1rem;
        margin-bottom: 1.25rem;
        flex-wrap: wrap;
      }
      .page-head-left {
        flex: 1;
        min-width: 0;
      }
      .page-head h1 {
        font-family: var(--font-display);
        font-weight: 800;
        font-size: clamp(1.65rem, 4.5vw, 2.1rem);
        line-height: 1.05;
        letter-spacing: -0.025em;
        margin: 0 0 0.3rem;
        color: var(--s-ink);
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .page-head .crumb {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.85rem;
        color: var(--s-ink-soft);
        font-weight: 500;
      }
      .page-head .live {
        display: inline-flex;
        align-items: center;
        gap: 0.55rem;
        padding: 6px 12px;
        background: var(--s-canvas-soft);
        border: 1px solid var(--s-line);
        border-radius: 999px;
        font-size: 0.78rem;
        font-weight: 600;
        color: var(--s-ink-soft);
        font-variant-numeric: tabular-nums;
      }

      /* ===== KPI strip (4 tiles) ===== */
      .kpi-strip {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 0.75rem;
        margin-bottom: 1rem;
      }
      @media (max-width: 780px) {
        .kpi-strip {
          grid-template-columns: 1fr 1fr;
        }
      }
      @media (max-width: 380px) {
        .kpi-strip {
          grid-template-columns: 1fr;
        }
      }
      .kpi-tile {
        padding: 1rem 1.15rem;
        min-height: 96px;
      }
      .kpi-tile .kpi-value {
        font-size: 1.7rem;
      }
      .kpi-tile .icon-bubble {
        position: absolute;
        right: 12px;
        top: 12px;
        width: 28px;
        height: 28px;
        border-radius: 9px;
        background: var(--s-canvas-soft);
        color: var(--s-ink-soft);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
      }
      .kpi-tile.accent-gold .icon-bubble {
        background: rgba(255, 255, 255, 0.12);
        color: var(--s-gold-bright);
      }

      /* ===== Panels (2-column) ===== */
      .panels {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.85rem;
      }
      @media (max-width: 780px) {
        .panels {
          grid-template-columns: 1fr;
        }
      }
      .panel {
        background: var(--s-canvas);
        border: 1px solid var(--s-line);
        border-radius: 14px;
        padding: 1rem 1.15rem 0.95rem;
        box-shadow: var(--s-shadow-soft);
        display: flex;
        flex-direction: column;
        min-height: 200px;
      }
      .panel-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 0.85rem;
      }
      .panel-title {
        font-family: var(--font-display);
        font-weight: 700;
        font-size: 0.95rem;
        color: var(--s-ink);
        letter-spacing: -0.005em;
      }

      ul.list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 1px;
      }
      ul.list li {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        padding: 0.55rem 0;
        font-size: 0.9rem;
        border-bottom: 1px solid var(--s-line-soft);
      }
      ul.list li:last-child {
        border-bottom: none;
      }
      ul.list .name {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--s-ink);
        font-weight: 500;
      }
      ul.list .num {
        font-variant-numeric: tabular-nums;
        font-weight: 700;
        color: var(--s-ink);
        font-size: 0.9rem;
      }
      ul.list .num.warn {
        color: var(--s-warn);
      }
      ul.list .meta {
        font-size: 0.78rem;
        color: var(--s-ink-faint);
        font-weight: 500;
      }

      .empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        flex: 1;
        color: var(--s-ink-faint);
        font-size: 0.88rem;
        text-align: center;
        padding: 1.5rem 0.5rem;
      }
      .empty sl-icon {
        font-size: 1.6rem;
        opacity: 0.5;
      }

      /* ===== Realtime ticker ===== */
      .ticker-wrap {
        margin-top: 1rem;
      }
      .ticker {
        max-height: 220px;
        overflow-y: auto;
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: 0.75rem;
        background: var(--s-canvas-soft);
        border: 1px solid var(--s-line);
        border-radius: 12px;
        padding: 0.7rem 0.9rem;
      }
      .ticker-entry {
        display: block;
        padding: 3px 0;
        color: var(--s-ink-soft);
      }
      .ticker-entry strong {
        color: var(--s-gold);
        font-weight: 700;
      }
      .ticker-empty {
        color: var(--s-ink-faint);
        font-style: italic;
      }

      .no-shop {
        padding: 1.5rem;
        background: var(--s-canvas-soft);
        border: 1px solid var(--s-line);
        border-radius: 14px;
        color: var(--s-ink-soft);
        font-size: 0.95rem;
        text-align: center;
      }
    `,
  ];

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('visibilitychange', this._onVisibilityChange);
    void this._refreshAll();
    this._startTimers();
    this._wireRealtime();
  }
  disconnectedCallback(): void {
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    this._stopTimers();
    for (const u of this._unsubRealtime) {
      try {
        u();
      } catch {
        /* gone */
      }
    }
    this._unsubRealtime = [];
    super.disconnectedCallback();
  }
  updated(changed: Map<string, unknown>) {
    super.updated?.(changed);
    if (changed.has('activeShop')) void this._refreshAll();
    if (changed.has('realtime')) {
      for (const u of this._unsubRealtime) {
        try {
          u();
        } catch {
          /* gone */
        }
      }
      this._unsubRealtime = [];
      this._wireRealtime();
    }
  }
  private _wireRealtime() {
    if (!this.realtime) return;
    const eventsOfInterest = ['ping', 'productAvailabilityChanged'];
    for (const name of eventsOfInterest) {
      this._unsubRealtime.push(
        this.realtime.on(name, (payload) => {
          this._pushTicker(name, payload);
          if (name === 'productAvailabilityChanged')
            void this._refreshUnavailable();
        })
      );
    }
  }
  private _pushTicker(eventName: string, payload: unknown) {
    this._ticker = [
      { receivedAt: new Date(), eventName, payload },
      ...this._ticker.slice(0, 9),
    ];
  }
  private _startTimers() {
    this._stopTimers();
    this._activeOrdersTimer = window.setInterval(
      () => void this._refreshActiveOrders(),
      20_000
    );
    this._salesTimer = window.setInterval(
      () => void this._refreshSalesReport(),
      60_000
    );
    this._lowStockTimer = window.setInterval(
      () => void this._refreshLowStock(),
      60_000
    );
    this._availabilityTimer = window.setInterval(
      () => void this._refreshUnavailable(),
      60_000
    );
  }
  private _stopTimers() {
    if (this._activeOrdersTimer) window.clearInterval(this._activeOrdersTimer);
    if (this._salesTimer) window.clearInterval(this._salesTimer);
    if (this._lowStockTimer) window.clearInterval(this._lowStockTimer);
    if (this._availabilityTimer) window.clearInterval(this._availabilityTimer);
    this._activeOrdersTimer =
      this._salesTimer =
      this._lowStockTimer =
      this._availabilityTimer =
        undefined;
  }
  private async _refreshAll() {
    if (!this.activeShop) return;
    await Promise.all([
      this._refreshActiveOrders(),
      this._refreshSalesReport(),
      this._refreshLowStock(),
      this._refreshUnavailable(),
    ]);
    this._lastRefreshUtc = new Date();
  }
  private async _refreshActiveOrders() {
    if (!this.activeShop) return;
    const r = await ApiFactory.authenticated().listActiveOrders(
      this.activeShop.shopName
    );
    if (r.success && r.data) this._activeOrders = r.data;
  }
  private async _refreshSalesReport() {
    if (!this.activeShop) return;
    const today = new Date().toISOString().slice(0, 10);
    const r = await ApiFactory.authenticated().salesReport(
      this.activeShop.shopName,
      today,
      today
    );
    if (r.success && r.data) this._salesReport = r.data;
  }
  private async _refreshLowStock() {
    if (!this.activeShop) return;
    const r = await ApiFactory.authenticated().lowStockReport(
      this.activeShop.shopName
    );
    if (r.success && r.data) this._lowStock = r.data;
  }
  private async _refreshUnavailable() {
    if (!this.activeShop) return;
    const r = await ApiFactory.authenticated().listProductAvailability(
      this.activeShop.shopName
    );
    if (r.success && r.data) this._unavailableItems = r.data;
  }

  private _oldestActiveOrder(): { ageMinutes: number } | null {
    if (this._activeOrders.length === 0) return null;
    let oldest = Infinity;
    for (const o of this._activeOrders) {
      const t = new Date(o.paidUtc || o.saleDate).getTime();
      if (t < oldest) oldest = t;
    }
    if (!isFinite(oldest)) return null;
    return { ageMinutes: Math.round((Date.now() - oldest) / 60_000) };
  }
  private _activeOrdersTotal(): number {
    return this._activeOrders.reduce((s, o) => s + (o.total ?? 0), 0);
  }
  private _money(n: number): string {
    return `${activeCurrency().symbol}${n.toLocaleString('en-PH', { maximumFractionDigits: 0 })}`;
  }

  // Compact human duration from minutes: "4d 20h", "3h 12m", "45m", "<1m".
  // Shoelace's <sl-relative-time> is single-unit relative-to-now ("5 days ago"),
  // not a two-unit age, so we format it ourselves.
  private _age(minutes: number): string {
    if (minutes < 1) return '<1m';
    const d = Math.floor(minutes / 1440);
    const h = Math.floor((minutes % 1440) / 60);
    const m = Math.floor(minutes % 60);
    if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
    if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
    return `${m}m`;
  }

  render() {
    if (!this.activeShop) {
      return html`
        <div class="shop-page">
          <div class="no-shop">Pick a shop to see the dashboard.</div>
        </div>
      `;
    }

    const oldest = this._oldestActiveOrder();
    const activeTotal = this._activeOrdersTotal();
    const isLive = this.realtime?.isConnected ?? false;
    const refreshLabel = this._lastRefreshUtc
      ? `Refreshed ${this._lastRefreshUtc.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      : 'Loading…';

    return html`
      <div class="shop-page">
        <div class="page-head">
          <div class="page-head-left">
            <h1>
              ${this.activeShop.shopDisplayName || this.activeShop.shopName}
            </h1>
            <span class="crumb">
              <sl-icon name="speedometer2"></sl-icon>
              Dashboard · live monitor
            </span>
          </div>
          <span class="live">
            <span class="live-dot ${isLive ? '' : 'offline'}"></span>
            ${refreshLabel}
          </span>
        </div>

        <!-- ===== KPI tiles ===== -->
        <div class="kpi-strip">
          <div class="kpi-tile accent-gold">
            <span class="icon-bubble"
              ><sl-icon name="cart-fill"></sl-icon
            ></span>
            <span class="kpi-label">Today · sales</span>
            <span class="kpi-value"
              >${this._salesReport?.completedCount ?? 0}</span
            >
            <span class="kpi-sub"
              >${this._money(this._salesReport?.grossSales ?? 0)} gross</span
            >
          </div>
          <div class="kpi-tile">
            <span class="icon-bubble"
              ><sl-icon name="hourglass-split"></sl-icon
            ></span>
            <span class="kpi-label">Active orders</span>
            <span class="kpi-value">${this._activeOrders.length}</span>
            <span class="kpi-sub">${this._money(activeTotal)} in flight</span>
          </div>
          <div class="kpi-tile">
            <span class="icon-bubble"
              ><sl-icon name="clock-history"></sl-icon
            ></span>
            <span class="kpi-label">Oldest unfinished</span>
            <span class="kpi-value"
              >${oldest ? this._age(oldest.ageMinutes) : '—'}</span
            >
            <span class="kpi-sub"
              >${oldest ? 'awaiting close' : 'all caught up'}</span
            >
          </div>
          <div class="kpi-tile">
            <span class="icon-bubble"><sl-icon name="graph-up"></sl-icon></span>
            <span class="kpi-label">Profit · today</span>
            <span class="kpi-value"
              >${this._money(this._salesReport?.grossProfit ?? 0)}</span
            >
            <span class="kpi-sub"
              >${this._salesReport?.completedCount ? 'live' : 'no sales yet'}</span
            >
          </div>
        </div>

        <!-- ===== Panels ===== -->
        <div class="panels">
          <div class="panel">
            <div class="panel-head">
              <span class="panel-title">Low stock</span>
              ${
                this._lowStock && this._lowStock.items.length > 0
                  ? html`<span class="status-pill warn"
                      ><span class="dot"></span
                      >${this._lowStock.items.length}</span
                    >`
                  : html`<span class="status-pill good"
                      ><span class="dot"></span>OK</span
                    >`
              }
            </div>
            ${
              this._lowStock && this._lowStock.items.length > 0
                ? html`
                    <ul class="list">
                      ${this._lowStock.items.slice(0, 6).map(
                        (i) => html`
                          <li>
                            <span class="name">${i.name}</span>
                            <span class="num warn"
                              >${i.onHand}/${i.reorderLevel} ${i.baseUnit}</span
                            >
                          </li>
                        `
                      )}
                    </ul>
                  `
                : html`<div class="empty">
                    <sl-icon name="check-circle"></sl-icon> Nothing below
                    reorder level.
                  </div>`
            }
          </div>

          <div class="panel">
            <div class="panel-head">
              <span class="panel-title">Currently sold out</span>
              ${
                this._unavailableItems.length > 0
                  ? html`<span class="status-pill bad"
                      ><span class="dot"></span
                      >${this._unavailableItems.length}</span
                    >`
                  : html`<span class="status-pill good"
                      ><span class="dot"></span>All on</span
                    >`
              }
            </div>
            ${
              this._unavailableItems.length > 0
                ? html`
                    <ul class="list">
                      ${this._unavailableItems.slice(0, 6).map(
                        (a) => html`
                          <li>
                            <span class="name">${a.itemId}</span>
                            <span class="meta">${a.reason}</span>
                          </li>
                        `
                      )}
                    </ul>
                  `
                : html`<div class="empty">
                    <sl-icon name="check-circle"></sl-icon> Everything is
                    available.
                  </div>`
            }
          </div>
        </div>

        <!-- ===== Realtime ticker (compact, subtle) ===== -->
        <div class="ticker-wrap">
          <div class="panel">
            <div class="panel-head">
              <span class="panel-title">Realtime activity</span>
              <span
                class="status-pill ${this.realtime?.isConnected ? 'good' : 'neutral'}"
              >
                <span class="dot"></span>
                ${this.realtime?.status.disabled ? 'disabled' : this.realtime?.isConnected ? 'live' : 'offline'}
              </span>
            </div>
            <div class="ticker">
              ${
                this._ticker.length === 0
                  ? html`<span class="ticker-empty">No events yet.</span>`
                  : this._ticker.map(
                      (e) => html`
                        <span class="ticker-entry">
                          ${e.receivedAt.toLocaleTimeString()} ·
                          <strong>${e.eventName}</strong> ·
                          ${JSON.stringify(e.payload)}
                        </span>
                      `
                    )
              }
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
